/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/AmeAviLogo.png";
import heroBg from "@/assets/images/hero-bg.jpg";
import discoveryFlightBg from "@/assets/images/dreaming-of-being-a-pilot.jpg";
import pilotSchool from "@/assets/images/pilot-school.jpg";
import footerBg from "@/assets/images/footer-bg.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/wp-content/uploads/2021/07/AmeAviLogo.png": logo,
  "/images/logo.png": logo,
  "/assets/images/AmeAviLogo.png": logo,
  "/logo.png": logo,
  
  // Hero background mappings
  "/wp-content/uploads/2021/07/about-us-header.jpg": heroBg,
  "/images/hero-bg.jpg": heroBg,
  "/assets/images/hero-bg.jpg": heroBg,
  
  // Discovery flight background mappings
  "/wp-content/uploads/2021/07/dreaming-of-being-a-pilot.jpg": discoveryFlightBg,
  "/images/discovery-flight-bg.jpg": discoveryFlightBg,
  "/assets/images/discovery-flight-bg.jpg": discoveryFlightBg,
  
  // Pilot school/cockpit image mappings
  "/wp-content/uploads/2021/07/pilot-school-1024x678.jpg": pilotSchool,
  "/images/pilot-school.jpg": pilotSchool,
  "/assets/images/pilot-school.jpg": pilotSchool,
  
  // Footer background mappings
  "/wp-content/uploads/2021/07/footer-bg.jpg": footerBg,
  "/images/footer-bg.jpg": footerBg,
  "/assets/images/footer-bg.jpg": footerBg,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
